/**
 * Order Report Details Screen (Manager)
 * 
 * Optimized report details interface for managers with:
 * - Comprehensive table view of order items
 * - PDF and CSV export functionality
 * - Advanced filtering and sorting
 * - Professional UI/UX optimized for data analysis
 * 
 * @module OrderReportDetailsScreen
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  FileText,
  FileSpreadsheet,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  Store,
  User,
  DollarSign,
  Package,
  CheckCircle,
  AlertCircle,
  Loader,
  ChevronDown,
  ChevronUp,
  Edit,
  Plus,
  X,
  ShoppingCart,
  Minus,
  MapPin,
  Mail,
  Phone,
  Info,
  Clock,
  Trash2,
  Send
} from 'lucide-react';
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, limit as firestoreLimit } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PDFService } from '../../services/PDFService';
import { CSVExportService } from '../../services/CSVExportService';
import { OrderManagementService } from '../../services/OrderManagementService';
import { userService } from '../../services/UserService';
import { useAuth } from '../../contexts/AuthContext';
import { getTranslatedProductName } from '../../utils/translationHelpers';
import { formatOrderDateForDisplay, getLocalCalendarYMD, parseCalendarYMDToLocalDate } from '../../utils/calendarDate';
import { ThemeColors, Spacing, Typography, Shadows, BorderRadius } from '../../styles/ThemeStyles';
import { PageContainer, MainContent } from '../../styles/StyledComponents';
import toast, { Toaster } from 'react-hot-toast';
import { Product, getAllProducts, productToFormItem } from '../../services/ProductService';

/**
 * Styled Components
 */
const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.xl};
  padding-bottom: ${Spacing.lg};
  border-bottom: 2px solid ${ThemeColors.border.light};
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.lg};
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  background: white;
  border-radius: ${BorderRadius.medium};
  color: ${ThemeColors.text.primary};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.gray[100]};
    border-color: ${ThemeColors.primary.main};
    color: ${ThemeColors.primary.main};
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const Title = styled.h1`
  font-size: ${Typography.fontSize.xxxl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0;
`;

const OrderInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.xs};
`;

const OrderNumber = styled.div`
  font-size: ${Typography.fontSize.lg};
  font-weight: ${Typography.fontWeight.semiBold};
  color: ${ThemeColors.text.primary};
`;

const OrderMeta = styled.div`
  display: flex;
  gap: ${Spacing.md};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  flex-wrap: wrap;
`;

const HeaderRight = styled.div`
  display: flex;
  gap: ${Spacing.md};
  flex-wrap: wrap;
  align-items: flex-start;
`;

const SendToCSBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const SendToCSHint = styled.span`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  max-width: 280px;
  text-align: right;
  line-height: 1.35;
`;

const ExportButton = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'info' }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.lg};
  border: none;
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.sm};
  font-weight: ${Typography.fontWeight.medium};
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => {
    if (props.variant === 'primary') {
      return `
    background: ${ThemeColors.primary.main};
    color: white;
    
        &:hover:not(:disabled) {
      background: ${ThemeColors.primary.dark};
    }
      `;
    } else if (props.variant === 'success') {
      return `
        background: ${ThemeColors.success.main};
        color: white;
        
        &:hover:not(:disabled) {
          background: ${ThemeColors.success.dark};
        }
      `;
    } else if (props.variant === 'info') {
      return `
        background: ${ThemeColors.info.main};
        color: white;
        
        &:hover:not(:disabled) {
          background: ${ThemeColors.info.dark};
        }
      `;
    } else {
      return `
    background: white;
    color: ${ThemeColors.text.primary};
    border: 1px solid ${ThemeColors.border.medium};
    
        &:hover:not(:disabled) {
      background: ${ThemeColors.gray[100]};
      border-color: ${ThemeColors.primary.main};
      color: ${ThemeColors.primary.main};
    }
      `;
    }
  }}

  svg {
    width: 18px;
    height: 18px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const InfoCards = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.xl};
`;

const InfoCard = styled.div`
  background: white;
  padding: ${Spacing.lg};
  border-radius: ${BorderRadius.medium};
  border: 1px solid ${ThemeColors.border.light};
  box-shadow: ${Shadows.sm};
`;

const InfoCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  margin-bottom: ${Spacing.sm};
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.medium};
  text-transform: uppercase;
`;

const InfoCardValue = styled.div`
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
`;

const DetailsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${Spacing.xl};
  margin-bottom: ${Spacing.xl};
  background: white;
  padding: ${Spacing.xl};
  border-radius: ${BorderRadius.medium};
  border: 1px solid ${ThemeColors.border.light};
  box-shadow: ${Shadows.sm};

  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacing.md};
`;

const DetailsTitle = styled.h3`
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.text.primary};
  margin: 0 0 ${Spacing.sm} 0;
  padding-bottom: ${Spacing.xs};
  border-bottom: 2px solid ${ThemeColors.primary.main};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};

  svg {
    color: ${ThemeColors.primary.main};
  }
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr;
  gap: ${Spacing.sm} ${Spacing.lg};

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: ${Spacing.xs};
  }
`;

const DetailsLabel = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.secondary};
  font-weight: ${Typography.fontWeight.medium};
`;

const DetailsValue = styled.div`
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  word-break: break-word;
`;

const FiltersBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
  flex-wrap: wrap;
`;

const SearchBar = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  svg {
    position: absolute;
    left: ${Spacing.md};
    top: 50%;
    transform: translateY(-50%);
    color: ${ThemeColors.text.secondary};
    width: 18px;
    height: 18px;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm} ${Spacing.md} ${Spacing.sm} 40px;
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.sm};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }
`;

const FilterSelect = styled.select`
  padding: ${Spacing.sm} ${Spacing.md};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.sm};
  background: white;
  color: ${ThemeColors.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const TableContainer = styled.div`
  background: white;
  border-radius: ${BorderRadius.medium};
  border: 1px solid ${ThemeColors.border.light};
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${ThemeColors.gray[100]};
  border-bottom: 2px solid ${ThemeColors.border.medium};
  
  th {
    padding: ${Spacing.md};
    text-align: left;
    font-weight: ${Typography.fontWeight.semiBold};
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.primary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    white-space: nowrap;
    cursor: pointer;
    user-select: none;
    vertical-align: middle;
    
    &:hover {
      background: ${ThemeColors.gray[200]};
    }
  }
`;

const SortableHeader = styled.th`
  display: table-cell;
`;

const SortableHeaderContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: ${Spacing.xs};
`;

const TableBody = styled.tbody`
  tr {
    border-bottom: 1px solid ${ThemeColors.border.light};
    transition: background-color 0.15s ease;
    
    &:hover {
      background: ${ThemeColors.gray[50]};
    }
    
    &:last-child {
      border-bottom: none;
    }
  }
  
  td {
    padding: ${Spacing.md};
    font-size: ${Typography.fontSize.sm};
    color: ${ThemeColors.text.primary};
    vertical-align: middle;
  }
`;

const CategoryBadge = styled.span<{ category: string }>`
  display: inline-block;
  padding: ${Spacing.xs} ${Spacing.sm};
  border-radius: ${BorderRadius.small};
  font-size: ${Typography.fontSize.xs};
  font-weight: ${Typography.fontWeight.bold};
  background: ${props => {
    switch (props.category) {
      case 'H': return ThemeColors.primary.light;
      case 'L': return ThemeColors.info.light;
      case 'F': return ThemeColors.success.light;
      case 'V': return ThemeColors.warning.light;
      case 'S': return ThemeColors.secondary.light;
      default: return ThemeColors.gray[200];
    }
  }};
  color: ${props => {
    switch (props.category) {
      case 'H': return ThemeColors.primary.dark;
      case 'L': return ThemeColors.info.dark;
      case 'F': return ThemeColors.success.dark;
      case 'V': return ThemeColors.warning.dark;
      case 'S': return ThemeColors.secondary.dark;
      default: return ThemeColors.text.primary;
    }
  }};
`;

const SummaryRow = styled.tr`
  background: ${ThemeColors.gray[50]};
  font-weight: ${Typography.fontWeight.bold};
  
  td {
    padding: ${Spacing.lg};
    font-size: ${Typography.fontSize.md};
    border-top: 2px solid ${ThemeColors.border.medium};
  }
`;

const TotalRow = styled.tr`
  background: ${ThemeColors.primary.light};
  font-weight: ${Typography.fontWeight.bold};
  
  td {
    padding: ${Spacing.lg};
    font-size: ${Typography.fontSize.lg};
    color: ${ThemeColors.primary.dark};
    border-top: 3px solid ${ThemeColors.primary.main};
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  gap: ${Spacing.md};
`;

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  gap: ${Spacing.md};
  text-align: center;
`;

const ErrorText = styled.div`
  color: ${ThemeColors.error.main};
  font-size: ${Typography.fontSize.md};
`;

const EmptyState = styled.div`
  padding: ${Spacing.xxl};
  text-align: center;
  color: ${ThemeColors.text.secondary};
`;

// Product Search Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${Spacing.lg};
`;

const ModalContent = styled.div`
  background: white;
  border-radius: ${BorderRadius.large};
  width: 100%;
  max-width: 900px;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${Shadows.xl};
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${Spacing.lg};
  border-bottom: 1px solid ${ThemeColors.border.light};
  background: linear-gradient(135deg, ${ThemeColors.primary.main}, ${ThemeColors.primary.dark});
  color: white;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${Typography.fontSize.xl};
  font-weight: ${Typography.fontWeight.bold};
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
`;

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: ${BorderRadius.small};
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

const ModalBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${Spacing.lg};
`;

const ProductSearchBar = styled.div`
  display: flex;
  gap: ${Spacing.md};
  margin-bottom: ${Spacing.lg};
`;

const ProductSearchInput = styled.input`
  flex: 1;
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.md};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
    box-shadow: 0 0 0 3px ${ThemeColors.primary.light}40;
  }
`;

const ProductCategorySelect = styled.select`
  padding: ${Spacing.md};
  border: 2px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.md};
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: ${Spacing.md};
  max-height: 400px;
  overflow-y: auto;
  padding-right: ${Spacing.sm};
`;

const ProductCard = styled.div<{ isSelected?: boolean }>`
  background: ${props => props.isSelected ? ThemeColors.primary.light : ThemeColors.gray[50]};
  border: 2px solid ${props => props.isSelected ? ThemeColors.primary.main : ThemeColors.border.light};
  border-radius: ${BorderRadius.medium};
  padding: ${Spacing.md};
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    border-color: ${ThemeColors.primary.main};
    box-shadow: ${Shadows.sm};
  }
`;

const ProductCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${Spacing.sm};
`;

const ProductName = styled.div`
  font-weight: ${Typography.fontWeight.semiBold};
  font-size: ${Typography.fontSize.sm};
  color: ${ThemeColors.text.primary};
  flex: 1;
  padding-right: ${Spacing.sm};
`;

const ProductCode = styled.div`
  font-size: ${Typography.fontSize.xs};
  color: ${ThemeColors.text.secondary};
  background: ${ThemeColors.gray[200]};
  padding: 2px 6px;
  border-radius: ${BorderRadius.small};
`;

const ProductDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${Spacing.sm};
`;

const ProductPrice = styled.div`
  font-weight: ${Typography.fontWeight.bold};
  color: ${ThemeColors.primary.main};
  font-size: ${Typography.fontSize.md};
`;

const ProductCategory = styled.span<{ category: string }>`
  font-size: ${Typography.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${BorderRadius.small};
  font-weight: ${Typography.fontWeight.medium};
  background: ${props => {
    switch (props.category) {
      case 'Heritage': return ThemeColors.primary.light;
      case 'Livingston': return ThemeColors.info.light;
      case 'Flower': return ThemeColors.success.light;
      case 'Vegetable': return ThemeColors.warning.light;
      default: return ThemeColors.gray[200];
    }
  }};
  color: ${props => {
    switch (props.category) {
      case 'Heritage': return ThemeColors.primary.dark;
      case 'Livingston': return ThemeColors.info.dark;
      case 'Flower': return ThemeColors.success.dark;
      case 'Vegetable': return ThemeColors.warning.dark;
      default: return ThemeColors.text.primary;
    }
  }};
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs};
  margin-top: ${Spacing.sm};
`;

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid ${ThemeColors.border.medium};
  background: white;
  border-radius: ${BorderRadius.small};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${ThemeColors.primary.light};
    border-color: ${ThemeColors.primary.main};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuantityInput = styled.input`
  width: 50px;
  text-align: center;
  padding: ${Spacing.xs};
  border: 1px solid ${ThemeColors.border.medium};
  border-radius: ${BorderRadius.small};
  font-size: ${Typography.fontSize.sm};

  &:focus {
    outline: none;
    border-color: ${ThemeColors.primary.main};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${Spacing.lg};
  border-top: 1px solid ${ThemeColors.border.light};
  background: ${ThemeColors.gray[50]};
`;

const SelectedProductsInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  color: ${ThemeColors.text.secondary};
  font-size: ${Typography.fontSize.sm};
`;

const AddProductsButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm};
  padding: ${Spacing.md} ${Spacing.xl};
  background: ${ThemeColors.primary.main};
  color: white;
  border: none;
  border-radius: ${BorderRadius.medium};
  font-size: ${Typography.fontSize.md};
  font-weight: ${Typography.fontWeight.semiBold};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${ThemeColors.primary.dark};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const NoProductsMessage = styled.div`
  text-align: center;
  padding: ${Spacing.xl};
  color: ${ThemeColors.text.secondary};
`;

const LoadingProducts = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${Spacing.xxl};
  gap: ${Spacing.md};
`;

/**
 * Order Report Details Screen Component
 */
export const OrderReportDetailsScreen: React.FC = () => {
  const { storeId, orderId } = useParams<{ storeId: string; orderId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const isEditMode = searchParams.get('edit') === 'true';
  
  const [orderData, setOrderData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<string>('category');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingCSV, setExportingCSV] = useState(false);
  const [sendingToCS, setSendingToCS] = useState(false);
  const [approving, setApproving] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  
  // Product search modal state
  const [showProductModal, setShowProductModal] = useState(false);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productCategoryFilter, setProductCategoryFilter] = useState<string>('all');
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<Map<string, number>>(new Map()); // ItemCode -> quantity

  // Resolved display names (email or username) for manager who approved and user who submitted
  const [resolvedManagerDisplay, setResolvedManagerDisplay] = useState<string | null>(null);
  const [resolvedSubmitterDisplay, setResolvedSubmitterDisplay] = useState<string | null>(null);
  const [resolvedSubmitterEmail, setResolvedSubmitterEmail] = useState<string | null>(null);

  // Resolve manager (reviewedBy) and submitter (finalizedBy) to email/username for display
  useEffect(() => {
    if (!orderData) {
      setResolvedManagerDisplay(null);
      setResolvedSubmitterDisplay(null);
      setResolvedSubmitterEmail(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      const managerId = orderData.reviewedBy;
      const submitterId = orderData.finalizedBy;
      if (managerId && !orderData.reviewedByName) {
        try {
          const manager = await userService.getUserById(managerId);
          if (!cancelled && manager) {
            const display = manager.email || manager.username || [manager.firstName, manager.lastName].filter(Boolean).join(' ') || managerId;
            setResolvedManagerDisplay(display);
          } else if (!cancelled) setResolvedManagerDisplay(null);
        } catch {
          if (!cancelled) setResolvedManagerDisplay(null);
        }
      } else {
        setResolvedManagerDisplay(null);
      }
      if (submitterId) {
        try {
          const submitter = await userService.getUserById(submitterId);
          if (!cancelled && submitter) {
            setResolvedSubmitterDisplay(submitter.email || submitter.username || [submitter.firstName, submitter.lastName].filter(Boolean).join(' ') || submitterId);
            setResolvedSubmitterEmail(submitter.email || null);
          } else if (!cancelled) {
            setResolvedSubmitterDisplay(null);
            setResolvedSubmitterEmail(null);
          }
        } catch {
          if (!cancelled) {
            setResolvedSubmitterDisplay(null);
            setResolvedSubmitterEmail(null);
          }
        }
      } else {
        setResolvedSubmitterDisplay(null);
        setResolvedSubmitterEmail(null);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [orderData?.reviewedBy, orderData?.reviewedByName, orderData?.finalizedBy]);

  // Coerce any date value (YYYY-MM-DD string, Timestamp, or Date) to a YYYY-MM-DD string
  // so HTML date inputs are always pre-populated correctly.
  const toYMD = (value: any): string => {
    if (!value) return '';
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (parseCalendarYMDToLocalDate(trimmed)) return trimmed;
      const parsed = new Date(trimmed);
      return Number.isNaN(parsed.getTime()) ? '' : getLocalCalendarYMD(parsed);
    }
    if (typeof value === 'object' && 'toDate' in value) {
      return getLocalCalendarYMD(value.toDate());
    }
    if (value instanceof Date) {
      return getLocalCalendarYMD(value);
    }
    return '';
  };

  // Initialize editing order when entering edit mode
  useEffect(() => {
    if (isEditMode && orderData && !editingOrder) {
      setEditingOrder({
        heritageItems: [...(orderData.heritageItems || [])],
        livingstonItems: [...(orderData.livingstonItems || [])],
        flowerItems: [...(orderData.flowerItems || [])],
        vegetableItems: [...(orderData.vegetableItems || [])],
        supplyItems: [...(orderData.supplyItems || [])],
        orderType: (orderData.orderType === 'custOrder' || orderData.orderType === 'offCarDelivery')
          ? orderData.orderType
          : 'custOrder',
        poNumber: orderData.poNumber || '',
        orderDate: toYMD(orderData.orderDate),
        deliveryDate: toYMD(orderData.deliveryDate),
        specialInstructions: orderData.specialInstructions || ''
      });
    } else if (!isEditMode && editingOrder) {
      setEditingOrder(null);
    }
  }, [isEditMode, orderData]);

  // Load order data
  useEffect(() => {
    const loadOrder = async () => {
      if (!storeId || !orderId) {
        setError('Store ID and Order ID are required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        // Try to load from OrderHistory
        const orderRef = doc(db, 'Stores', storeId, 'OrderHistory', orderId);
        const orderSnap = await getDoc(orderRef);
        
        if (orderSnap.exists()) {
          const data = orderSnap.data();
          
          // Normalize status: "submitted" should be treated as "pending"
          const normalizedStatus = data.status === 'submitted' ? 'pending' : (data.status || 'pending');
          
          setOrderData({
            ...data,
            orderId: data.orderId || orderSnap.id || orderId,
            status: normalizedStatus,
            _documentId: orderSnap.id // Store the actual document ID for updates
          });
        } else {
          // Try searching by orderId field
          const historyRef = collection(db, 'Stores', storeId, 'OrderHistory');
          const q = query(historyRef, where('orderId', '==', orderId));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            
            // Normalize status: "submitted" should be treated as "pending"
            const normalizedStatus = data.status === 'submitted' ? 'pending' : (data.status || 'pending');
            
            setOrderData({
              ...data,
              orderId: data.orderId || doc.id || orderId,
              status: normalizedStatus,
              _documentId: doc.id // Store the actual document ID for updates
            });
          } else {
            setError('Order not found');
          }
        }
      } catch (err: any) {
        console.error('Error loading order:', err);
        setError(err.message || 'Failed to load order');
      } finally {
        setLoading(false);
      }
    };

    loadOrder();
  }, [storeId, orderId]);

  // Format timestamp helper function
  const formatTimestamp = (date: Date | Timestamp | string | undefined): string | null => {
    if (!date) return null;
    
    let dateObj: Date;
    
    if (date instanceof Date) {
      dateObj = date;
    } else if (typeof date === 'string') {
      dateObj = new Date(date);
    } else if (date && typeof date === 'object' && 'toDate' in date) {
      dateObj = (date as any).toDate();
    } else {
      return null;
    }
    
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const hours = String(dateObj.getHours()).padStart(2, '0');
    const minutes = String(dateObj.getMinutes()).padStart(2, '0');
    
    return `${year}${month}${day}${hours}${minutes}`;
  };

  // Get order identifier - always use orderId (timestamp format YYYYMMDDHHMM)
  const getOrderIdentifier = useMemo(() => {
    if (!orderData) return orderId || '';
    
    // Always use orderId as the primary identifier (should be timestamp format)
    if (orderData.orderId && /^\d{12}$/.test(orderData.orderId)) {
      return orderData.orderId;
    }
    
    // If orderId is not in timestamp format, generate it from order date
    const orderDate = orderData.finalizedAt || orderData.orderDate || orderData.createdAt || orderData.updatedAt;
    const timestamp = formatTimestamp(orderDate);
    
    if (timestamp) {
      return timestamp;
    }
    
    // Fallback to existing orderId or orderId from URL
    return orderData.orderId || orderId || '';
  }, [orderData, orderId]);

  // Prepare all order items with category information
  const allItems = useMemo(() => {
    if (!orderData) return [];
    
    const items: any[] = [];
    
    // Use editingOrder data if in edit mode, otherwise use orderData
    const sourceData = isEditMode && editingOrder ? editingOrder : orderData;
    
    // Add items with their categories
    (sourceData.heritageItems || []).forEach((item: any) => {
      items.push({ ...item, category: 'H', categoryName: t('categories.heritage') });
    });
    
    (sourceData.livingstonItems || []).forEach((item: any) => {
      items.push({ ...item, category: 'L', categoryName: 'Livingston' });
    });
    
    (sourceData.flowerItems || []).forEach((item: any) => {
      items.push({ ...item, category: 'F', categoryName: t('categories.flower') });
    });
    
    (sourceData.vegetableItems || []).forEach((item: any) => {
      items.push({ ...item, category: 'V', categoryName: t('categories.vegetable') });
    });

    (sourceData.supplyItems || []).forEach((item: any) => {
      items.push({ ...item, category: 'S', categoryName: t('categories.supplies', 'Supplies') });
    });
    
    return items;
  }, [orderData, editingOrder, isEditMode, t]);

  // Filter and sort items
  const filteredAndSortedItems = useMemo(() => {
    let filtered = allItems.filter(item => {
      // Search filter
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        (item.packetDescription || item.name || '').toLowerCase().includes(searchLower) ||
        (item.code || '').toLowerCase().includes(searchLower);
      
      // Category filter
      const matchesCategory = categoryFilter === 'all' || item.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });

    // Sort items
    filtered.sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortField) {
        case 'category':
          aValue = a.category;
          bValue = b.category;
          break;
        case 'name':
          aValue = (a.packetDescription || a.name || '').toLowerCase();
          bValue = (b.packetDescription || b.name || '').toLowerCase();
          break;
        case 'quantity':
          aValue = parseInt(a.quantity) || 0;
          bValue = parseInt(b.quantity) || 0;
          break;
        case 'price':
          aValue = parseFloat(a.price) || 0;
          bValue = parseFloat(b.price) || 0;
          break;
        case 'total':
          aValue = (parseFloat(a.price) || 0) * (parseInt(a.quantity) || 0);
          bValue = (parseFloat(b.price) || 0) * (parseInt(b.quantity) || 0);
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [allItems, searchTerm, categoryFilter, sortField, sortDirection]);

  // Calculate totals - use editingOrder data if in edit mode
  const totals = useMemo(() => {
    // Use filteredAndSortedItems which already uses editingOrder data when in edit mode
    const totalItems = filteredAndSortedItems.reduce((sum, item) => sum + (parseInt(item.quantity) || 0), 0);
    const totalAmount = filteredAndSortedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      return sum + (price * quantity);
    }, 0);
    
    // Category totals
    const categoryTotals: Record<string, { items: number; amount: number }> = {};
    filteredAndSortedItems.forEach(item => {
      if (!categoryTotals[item.category]) {
        categoryTotals[item.category] = { items: 0, amount: 0 };
      }
      categoryTotals[item.category].items += parseInt(item.quantity) || 0;
      const price = parseFloat(item.price) || 0;
      const quantity = parseInt(item.quantity) || 0;
      categoryTotals[item.category].amount += price * quantity;
    });
    
    return { totalItems, totalAmount, categoryTotals };
  }, [filteredAndSortedItems]);

  // Handle sort
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Handle edit order - toggle edit mode
  const handleEditOrder = () => {
    if (!orderData) return;
    if (isEditMode) {
      // Cancel edit mode
      setEditingOrder(null);
      navigate(`/manager/order-details/${storeId}/${orderId}`);
    } else {
      // Enter edit mode
      navigate(`/manager/order-details/${storeId}/${orderId}?edit=true`);
    }
  };

  // Handle save edited order
  const handleSaveOrder = async () => {
    if (!storeId || !orderId || !user?.id || !editingOrder || !orderData) return;

    try {
      setSaving(true);
      
      // Use the actual document ID from orderData if available, otherwise use orderId from URL
      // The document ID might be different from the orderId field
      const actualOrderId = orderData._documentId || orderId;
      
      const result = await OrderManagementService.updateOrder(
        storeId,
        actualOrderId,
        editingOrder,
        user.id
      );

      if (result.success) {
        toast.success('Order updated successfully');
        // Reload order data using the actual document ID
        const reloadOrderId = actualOrderId;
        const orderRef = doc(db, 'Stores', storeId, 'OrderHistory', reloadOrderId);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const data = orderSnap.data();
          
          // Normalize status: "submitted" should be treated as "pending"
          const normalizedStatus = data.status === 'submitted' ? 'pending' : (data.status || 'pending');
          
          setOrderData({
            ...data,
            orderId: data.orderId || orderSnap.id || orderId,
            status: normalizedStatus,
            _documentId: orderSnap.id // Store the actual document ID for future updates
          });
        } else {
          // If not found by document ID, try searching by orderId field
          const historyRef = collection(db, 'Stores', storeId, 'OrderHistory');
          const q = query(historyRef, where('orderId', '==', orderId), limit(1));
          const querySnapshot = await getDocs(q);
          
          if (!querySnapshot.empty) {
            const doc = querySnapshot.docs[0];
            const data = doc.data();
            
            const normalizedStatus = data.status === 'submitted' ? 'pending' : (data.status || 'pending');
            
            setOrderData({
              ...data,
              orderId: data.orderId || doc.id || orderId,
              status: normalizedStatus,
              _documentId: doc.id
            });
          }
        }
        // Exit edit mode
        setEditingOrder(null);
        navigate(`/manager/order-details/${storeId}/${orderId}`);
      } else {
        toast.error(result.error || 'Failed to update order');
      }
    } catch (error: any) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  // Load products when modal opens
  const loadProducts = useCallback(async () => {
    if (allProducts.length > 0) return; // Already loaded
    
    setLoadingProducts(true);
    try {
      const products = await getAllProducts();
      setAllProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoadingProducts(false);
    }
  }, [allProducts.length]);

  // Open product search modal
  const handleOpenProductModal = () => {
    setShowProductModal(true);
    setProductSearchTerm('');
    setProductCategoryFilter('all');
    setSelectedProducts(new Map());
    loadProducts();
  };

  // Close product search modal
  const handleCloseProductModal = () => {
    setShowProductModal(false);
    setSelectedProducts(new Map());
  };

  // Filter products based on search and category
  const filteredProducts = useMemo(() => {
    if (!allProducts.length) return [];
    
    return allProducts.filter(product => {
      // Search filter - by name or item code
      const searchLower = productSearchTerm.toLowerCase().trim();
      const matchesSearch = !searchLower || 
        product.ItemNameEN.toLowerCase().includes(searchLower) ||
        product.ItemNameFR.toLowerCase().includes(searchLower) ||
        product.ItemCode.toLowerCase().includes(searchLower);
      
      // Category filter
      const matchesCategory = productCategoryFilter === 'all' || 
        product.ItemForm === productCategoryFilter;
      
      return matchesSearch && matchesCategory;
    }).slice(0, 100); // Limit to 100 results for performance
  }, [allProducts, productSearchTerm, productCategoryFilter]);

  // Handle product quantity change
  const handleProductQuantityChange = (itemCode: string, quantity: number) => {
    const newSelected = new Map(selectedProducts);
    if (quantity > 0) {
      newSelected.set(itemCode, quantity);
    } else {
      newSelected.delete(itemCode);
    }
    setSelectedProducts(newSelected);
  };

  // Increment product quantity
  const incrementQuantity = (itemCode: string) => {
    const current = selectedProducts.get(itemCode) || 0;
    handleProductQuantityChange(itemCode, current + 1);
  };

  // Decrement product quantity
  const decrementQuantity = (itemCode: string) => {
    const current = selectedProducts.get(itemCode) || 0;
    if (current > 0) {
      handleProductQuantityChange(itemCode, current - 1);
    }
  };

  // Add selected products to order
  const handleAddProductsToOrder = () => {
    if (!editingOrder || selectedProducts.size === 0) return;
    
    const updatedOrder = { ...editingOrder };
    
    // Add each selected product to the appropriate category
    selectedProducts.forEach((quantity, itemCode) => {
      const product = allProducts.find(p => p.ItemCode === itemCode);
      if (!product || quantity <= 0) return;
      
      // Determine which category array to add to based on ItemForm
      let categoryKey: string;
      switch (product.ItemForm) {
        case 'Heritage':
          categoryKey = 'heritageItems';
          break;
        case 'Livingston':
          categoryKey = 'livingstonItems';
          break;
        case 'Flower':
          categoryKey = 'flowerItems';
          break;
        case 'Vegetable':
          categoryKey = 'vegetableItems';
          break;
        default:
          categoryKey = 'vegetableItems';
      }
      
      // Check if product already exists in the category
      const existingItems = updatedOrder[categoryKey] || [];
      const existingIndex = existingItems.findIndex((item: any) => item.code === itemCode);
      
      if (existingIndex >= 0) {
        // Update quantity if already exists
        existingItems[existingIndex].quantity = (existingItems[existingIndex].quantity || 0) + quantity;
      } else {
        // Add new product
        const newItem = productToFormItem(product, quantity);
        existingItems.push(newItem);
      }
      
      updatedOrder[categoryKey] = existingItems;
    });
    
    setEditingOrder(updatedOrder);
    handleCloseProductModal();
    toast.success(`Added ${selectedProducts.size} product(s) to order`);
  };

  // Check if order can be edited (not approved)
  const canEditOrder = useMemo(() => {
    if (!orderData) return false;
    const status = orderData.status?.toLowerCase();
    return status !== 'approved' && status !== 'completed' && status !== 'cancelled';
  }, [orderData]);

  // Handle approve order
  const handleApproveOrder = async () => {
    if (!storeId || !orderId || !user?.id || !orderData) {
      toast.error('Unable to approve order: missing information');
      return;
    }

    if (!window.confirm('Are you sure you want to approve this order?')) {
      return;
    }

    try {
      setApproving(true);
      
      // Use the actual document ID from orderData if available, otherwise use orderId from URL
      const actualOrderId = orderData._documentId || orderId;
      
      const managerDisplayName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || user.email || user.id;
      const result = await OrderManagementService.approveOrder(
        storeId,
        actualOrderId,
        user.id,
        undefined,
        managerDisplayName
      );

      if (result.success) {
        toast.success('Order approved successfully');
        // Reload order data to reflect new status (including reviewedByName)
        const reloadOrderId = actualOrderId;
        const orderRef = doc(db, 'Stores', storeId, 'OrderHistory', reloadOrderId);
        const orderSnap = await getDoc(orderRef);
        if (orderSnap.exists()) {
          const data = orderSnap.data();
          setOrderData({
            ...data,
            orderId: data.orderId || orderSnap.id || orderId,
            status: data.status || 'approved',
            reviewedByName: data.reviewedByName,
            _documentId: orderSnap.id
          });
        } else {
          const historyRef = collection(db, 'Stores', storeId, 'OrderHistory');
          const q = query(historyRef, where('orderId', '==', orderId), limit(1));
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docSnap = querySnapshot.docs[0];
            const data = docSnap.data();
            setOrderData({
              ...data,
              orderId: data.orderId || docSnap.id || orderId,
              status: data.status || 'approved',
              reviewedByName: data.reviewedByName,
              _documentId: docSnap.id
            });
          }
        }
      } else {
        toast.error(result.error || 'Failed to approve order');
      }
    } catch (error: any) {
      console.error('Error approving order:', error);
      toast.error('Failed to approve order');
    } finally {
      setApproving(false);
    }
  };

  // Handle delete order
  const handleDeleteOrder = async () => {
    if (!storeId || !orderId || !user?.id || !orderData) {
      toast.error('Unable to delete order: missing information');
      return;
    }

    if (!window.confirm('Are you sure you want to delete this order? This action cannot be undone.')) {
      return;
    }

    try {
      setSaving(true);
      
      const actualOrderId = orderData._documentId || orderId;
      
      const result = await OrderManagementService.deleteOrder(
        storeId,
        actualOrderId,
        user.id
      );

      if (result.success) {
        toast.success('Order deleted successfully');
        navigate('/manager/orders');
      } else {
        toast.error(result.error || 'Failed to delete order');
      }
    } catch (error: any) {
      console.error('Error deleting order:', error);
      toast.error('Failed to delete order');
    } finally {
      setSaving(false);
    }
  };

  const handleSendToCustomerService = async () => {
    if (!storeId || !orderData || !user?.id) return;
    if (orderData.status !== 'approved') return;

    try {
      setSendingToCS(true);
      const actualOrderId = orderData._documentId || orderId;
      const result = await OrderManagementService.sendApprovedOrderEmailToCustomerService(
        storeId,
        actualOrderId,
        user.id
      );

      if (result.success) {
        toast.success(
          t('orderReport.sendToCSSuccess', 'Sent to the portal and Customer Service successfully.')
        );
        setOrderData((prev: any) =>
          prev ? { ...prev, customerServiceEmailSentAt: Timestamp.now() } : prev
        );
      } else {
        toast.error(
          result.error ||
            t('orderReport.sendToCSError', 'Failed to send to Customer Service. Please try again.')
        );
      }
    } catch (error: any) {
      console.error('Error sending to Customer Service:', error);
      toast.error(
        error?.message ||
          t('orderReport.sendToCSError', 'Failed to send to Customer Service. Please try again.')
      );
    } finally {
      setSendingToCS(false);
    }
  };

  // Handle PDF export
  const handleExportPDF = async () => {
    if (!orderData) return;
    
    try {
      setExportingPDF(true);
      const orderIdentifier = String(getOrderIdentifier ?? orderData?.orderId ?? orderId ?? 'order');
      const pdfBlob = await PDFService.generateOrderPDF({
        orderData,
        orderId: orderIdentifier,
        t
      });
      
      const filename = `order_report_${orderIdentifier}_${new Date().toISOString().split('T')[0]}.pdf`;
      PDFService.downloadPDF(pdfBlob, filename);
      toast.success(t('orderReport.exportSuccess', 'PDF exported successfully.'));
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('orderReport.exportError', 'Failed to export PDF. Please try again.'));
    } finally {
      setExportingPDF(false);
    }
  };

  // Handle CSV export
  const handleExportCSV = async () => {
    if (!orderData) return;
    
    try {
      setExportingCSV(true);
      
      // Use orderId (timestamp format) as the identifier
      const orderIdentifier = getOrderIdentifier;
      
      // Prepare CSV data with better structure
      const csvRows: string[][] = [];
      
      // Title row
      csvRows.push(['ORDER REPORT']);
      csvRows.push([]); // Empty row
      
      // Customer Information Section
      csvRows.push(['CUSTOMER INFORMATION']);
      csvRows.push(['Field', 'Value']);
      csvRows.push(['Customer Number', orderData.customerNumber || orderData.storeId || '']);
      csvRows.push(['Store Name', orderData.storeName || '']);
      csvRows.push(['Address', orderData.address || '']);
      csvRows.push(['City', orderData.city || '']);
      csvRows.push(['Province', orderData.province || orderData.state || '']);
      csvRows.push(['Postal Code', orderData.postalCode || orderData.pc || '']);
      csvRows.push(['Email', orderData.email || orderData.customerEmail || '']);
      csvRows.push(['Phone', orderData.phoneNumber || orderData.phone || '']);
      csvRows.push([]); // Empty row separator
      
      // Order Information Section
      csvRows.push(['ORDER INFORMATION']);
      csvRows.push(['Field', 'Value']);
      csvRows.push(['Order ID', orderIdentifier]);
      if (orderData.poNumber && orderData.poNumber.trim() !== '') {
        csvRows.push(['PO Number', orderData.poNumber]);
      }
      csvRows.push(['Order Date', orderData.orderDate ? formatOrderDateForDisplay(orderData.orderDate) : '']);
      csvRows.push(['Delivery Date', orderData.deliveryDate ? formatOrderDateForDisplay(orderData.deliveryDate) : '']);
      csvRows.push(['Order Type', orderData.orderType === 'custOrder' ? 'Customer Order' : 
                    orderData.orderType === 'offCarDelivery' ? 'Off Car Delivery' : 
                    orderData.orderType || '']);
      csvRows.push(['Special Instructions', orderData.specialInstructions || '']);
      csvRows.push(['Status', orderData.status || 'Completed']);
      csvRows.push([]); // Empty row separator
      
      // Product Items Section
      csvRows.push(['PRODUCT ITEMS']);
      csvRows.push(['Category', 'Product Name', 'Code', 'Quantity', 'Unit Price', 'Total Price']);
      
      // Add product items
      filteredAndSortedItems.forEach(item => {
        const unitPrice = parseFloat(item.price) || 0;
        const quantity = parseInt(item.quantity) || 0;
        const totalPrice = unitPrice * quantity;
        
        csvRows.push([
          item.categoryName || item.category,
          getTranslatedProductName(item.packetDescription || item.name || '', t),
          item.code || '',
          quantity.toString(),
          unitPrice.toFixed(2),
          totalPrice.toFixed(2)
        ]);
      });
      
      // Add summary row
      csvRows.push([]); // Empty row
      csvRows.push(['TOTAL', '', '', totals.totalItems.toString(), '', totals.totalAmount.toFixed(2)]);
      
      // Convert to CSV format using CSVExportService
      const csvContent = csvRows.map(row => {
        return row.map(cell => {
          // Escape quotes and wrap in quotes if contains comma, quote, or newline
          const escaped = String(cell || '').replace(/"/g, '""');
          if (escaped.includes(',') || escaped.includes('"') || escaped.includes('\n')) {
            return `"${escaped}"`;
          }
          return escaped;
        }).join(',');
      }).join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        const filename = `order_report_${orderIdentifier}_${new Date().toISOString().split('T')[0]}.csv`;
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert(t('orderReport.exportError', 'Failed to export CSV. Please try again.'));
    } finally {
      setExportingCSV(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <MainContent>
          <LoadingContainer>
            <Loader size={48} className="spin" />
            <div>{t('orderReport.loading', 'Loading order details...')}</div>
          </LoadingContainer>
        </MainContent>
      </PageContainer>
    );
  }

  if (error || !orderData) {
    return (
      <PageContainer>
        <MainContent>
          <ErrorContainer>
            <AlertCircle size={48} color={ThemeColors.error.main} />
            <ErrorText>{error || t('orderReport.notFound', 'Order not found')}</ErrorText>
            <BackButton onClick={() => navigate('/manager/orders')}>
              <ArrowLeft />
              {t('orderReport.backToOrders', 'Back to Orders')}
            </BackButton>
          </ErrorContainer>
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Toaster position="top-right" />
      <MainContent>
        <Container>
          {/* Header */}
          <Header>
            <HeaderLeft>
              <BackButton onClick={() => navigate('/manager/orders')}>
                <ArrowLeft />
                {t('orderReport.back', 'Back')}
              </BackButton>
              <div>
                <Title>{t('orderReport.title', 'Order Report Details')}</Title>
                <OrderInfo>
                  <OrderNumber>
                    {t('orderReport.orderNumber', 'Order')}: {getOrderIdentifier}
                  </OrderNumber>
                  <OrderMeta>
                    {orderData.storeName && (
                      <span>
                        <Store size={14} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
                        {orderData.storeName}
                      </span>
                    )}
                    {orderData.orderDate && (
                      <span>
                        <Calendar size={14} style={{ marginRight: Spacing.xs, display: 'inline-block', verticalAlign: 'middle' }} />
                        {formatOrderDateForDisplay(orderData.orderDate)}
                      </span>
                    )}
                    {orderData.poNumber && (
                      <span>
                        PO: {orderData.poNumber}
                      </span>
                    )}
                  </OrderMeta>
                </OrderInfo>
              </div>
            </HeaderLeft>
            <HeaderRight>
              {(orderData?.status === 'pending' || orderData?.status === 'submitted') && !isEditMode && (
                <ExportButton 
                  variant="success" 
                  onClick={handleApproveOrder}
                  disabled={approving}
                >
                  {approving ? <Loader size={18} className="spin" /> : <CheckCircle />}
                  {t('orderReport.approveOrder', 'Approve Order')}
                </ExportButton>
              )}
              {isEditMode ? (
                <>
                  <ExportButton 
                    variant="primary" 
                    onClick={handleOpenProductModal}
                    disabled={saving}
                  >
                    <Plus size={18} />
                    {t('orderReport.addProducts', 'Add Products')}
                  </ExportButton>
                  <ExportButton 
                    variant="success" 
                    onClick={handleSaveOrder}
                    disabled={saving || !editingOrder}
                  >
                    {saving ? <Loader size={18} className="spin" /> : <CheckCircle />}
                    {t('orderReport.saveOrder', 'Save Changes')}
                  </ExportButton>
                  <ExportButton 
                    variant="secondary" 
                    onClick={handleEditOrder}
                    disabled={saving}
                  >
                    {t('orderReport.cancelEdit', 'Cancel')}
                  </ExportButton>
                </>
              ) : (
                <>
                  <ExportButton 
                    variant="info" 
                    onClick={handleEditOrder}
                    disabled={loading || !orderData || !canEditOrder}
                    title={!canEditOrder ? 'Order cannot be modified after approval' : ''}
                  >
                    <Edit />
                    {t('orderReport.editOrder', 'Edit Order')}
                  </ExportButton>
                  <ExportButton 
                    onClick={handleDeleteOrder}
                    disabled={loading || !orderData}
                    style={{ backgroundColor: ThemeColors.error.main, color: 'white' }}
                  >
                    <Trash2 />
                    {t('orderReport.deleteOrder', 'Delete Order')}
                  </ExportButton>
                  {orderData.status === 'approved' && (
                    <SendToCSBlock>
                      <ExportButton
                        variant="success"
                        onClick={handleSendToCustomerService}
                        disabled={sendingToCS || saving || orderData.customerServiceEmailSentAt != null}
                        title={t(
                          'orderReport.sendToCustomerServiceTitle',
                          'Email the approval pack to the portal inbox and Customer Service'
                        )}
                      >
                        {sendingToCS ? (
                          <Loader size={18} className="spin" />
                        ) : (
                          <Send size={18} />
                        )}
                        {t('orderReport.sendToCustomerService', 'Send to Customer Service')}
                      </ExportButton>
                      {orderData.customerServiceEmailSentAt != null && (
                        <SendToCSHint>
                          {t(
                            'orderReport.sendToCSHint',
                            'Already sent to the portal and Customer Service.'
                          )}
                          {formatTimestamp(orderData.customerServiceEmailSentAt) ? (
                            <span>
                              {' '}
                              (
                              {formatTimestamp(orderData.customerServiceEmailSentAt)})
                            </span>
                          ) : null}
                        </SendToCSHint>
                      )}
                    </SendToCSBlock>
                  )}
                </>
              )}
              <ExportButton 
                variant="secondary" 
                onClick={handleExportPDF}
                disabled={exportingPDF}
              >
                {exportingPDF ? <Loader size={18} className="spin" /> : <FileText />}
                {t('orderReport.exportPDF', 'Export PDF')}
              </ExportButton>
              <ExportButton 
                variant="primary" 
                onClick={handleExportCSV}
                disabled={exportingCSV}
              >
                {exportingCSV ? <Loader size={18} className="spin" /> : <FileSpreadsheet />}
                {t('orderReport.exportCSV', 'Export CSV')}
              </ExportButton>
            </HeaderRight>
          </Header>

          {/* Info Cards */}
          <InfoCards>
            <InfoCard>
              <InfoCardHeader>
                <Package size={16} />
                {t('orderReport.totalItems', 'Total Items')}
              </InfoCardHeader>
              <InfoCardValue>{totals.totalItems}</InfoCardValue>
            </InfoCard>
            <InfoCard>
              <InfoCardHeader>
                <DollarSign size={16} />
                {t('orderReport.totalAmount', 'Total Amount')}
              </InfoCardHeader>
              <InfoCardValue>${totals.totalAmount.toFixed(2)}</InfoCardValue>
            </InfoCard>
            <InfoCard>
              <InfoCardHeader>
                <Store size={16} />
                {t('orderReport.store', 'Store')}
              </InfoCardHeader>
              <InfoCardValue>{orderData.storeName || t('orderReport.na', 'N/A')}</InfoCardValue>
            </InfoCard>
            <InfoCard>
              <InfoCardHeader>
                <CheckCircle size={16} />
                {t('orderReport.status', 'Status')}
              </InfoCardHeader>
              <InfoCardValue style={{ textTransform: 'capitalize' }}>
                {orderData.status || t('orderReport.completed', 'Completed')}
              </InfoCardValue>
            </InfoCard>
          </InfoCards>

          {/* Customer and Order Details */}
          <DetailsContainer>
            {/* Customer Information Section */}
            <DetailsSection>
              <DetailsTitle>
                <User size={18} />
                {t('pdf.customerInformation', 'Customer Information')}
              </DetailsTitle>
              <DetailsGrid>
                <DetailsLabel>{t('pdf.customerHash', 'Customer #')}:</DetailsLabel>
                <DetailsValue>{orderData.customerNumber || orderData.storeId || t('pdf.na', 'N/A')}</DetailsValue>

                <DetailsLabel>{t('pdf.storeName', 'Store Name')}:</DetailsLabel>
                <DetailsValue>{orderData.storeName || t('pdf.na', 'N/A')}</DetailsValue>

                <DetailsLabel>{t('pdf.address', 'Address')}:</DetailsLabel>
                <DetailsValue>
                  {orderData.address || t('pdf.na', 'N/A')}<br />
                  {orderData.city && `${orderData.city}, `}
                  {(orderData.province || orderData.state) && `${orderData.province || orderData.state} `}
                  {(orderData.postalCode || orderData.pc) && `${orderData.postalCode || orderData.pc}`}
                </DetailsValue>

                <DetailsLabel>{t('pdf.email', 'Email')}:</DetailsLabel>
                <DetailsValue>
                  {orderData.email || orderData.customerEmail ? (
                    <a href={`mailto:${orderData.email || orderData.customerEmail}`} style={{ color: ThemeColors.primary.main, textDecoration: 'none' }}>
                      <Mail size={12} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                      {orderData.email || orderData.customerEmail}
                    </a>
                  ) : t('pdf.na', 'N/A')}
                </DetailsValue>

                <DetailsLabel>{t('pdf.phone', 'Phone')}:</DetailsLabel>
                <DetailsValue>
                  {orderData.phoneNumber || orderData.phone ? (
                    <a href={`tel:${orderData.phoneNumber || orderData.phone}`} style={{ color: ThemeColors.primary.main, textDecoration: 'none' }}>
                      <Phone size={12} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                      {orderData.phoneNumber || orderData.phone}
                    </a>
                  ) : t('pdf.na', 'N/A')}
                </DetailsValue>
              </DetailsGrid>
            </DetailsSection>

            {/* Order Information Section */}
            <DetailsSection>
              <DetailsTitle>
                <Info size={18} />
                {t('pdf.orderInformation', 'Order Information')}
              </DetailsTitle>
              <DetailsGrid>
                <DetailsLabel>{t('orderReport.orderNumber', 'Order ID')}:</DetailsLabel>
                <DetailsValue>{getOrderIdentifier}</DetailsValue>

                <DetailsLabel>{t('pdf.poNumber', 'PO Number')}:</DetailsLabel>
                <DetailsValue>
                  {isEditMode ? (
                    <input
                      type="text"
                      value={editingOrder?.poNumber ?? ''}
                      onChange={(e) => {
                        if (!editingOrder) return;
                        setEditingOrder({ ...editingOrder, poNumber: e.target.value });
                      }}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${ThemeColors.border.medium}`,
                        borderRadius: BorderRadius.small,
                        fontSize: Typography.fontSize.sm,
                        backgroundColor: 'white',
                        color: ThemeColors.text.primary,
                        width: '100%'
                      }}
                    />
                  ) : (
                    orderData.poNumber || t('pdf.na', 'N/A')
                  )}
                </DetailsValue>

                <DetailsLabel>{t('pdf.date', 'Order Date')}:</DetailsLabel>
                <DetailsValue>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={editingOrder?.orderDate ?? ''}
                      onChange={(e) => {
                        if (!editingOrder) return;
                        setEditingOrder({ ...editingOrder, orderDate: e.target.value });
                      }}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${ThemeColors.border.medium}`,
                        borderRadius: BorderRadius.small,
                        fontSize: Typography.fontSize.sm,
                        backgroundColor: 'white',
                        color: ThemeColors.text.primary,
                        width: '100%'
                      }}
                    />
                  ) : (
                    orderData.orderDate ? (
                      <>
                        <Calendar size={12} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                        {formatOrderDateForDisplay(orderData.orderDate)}
                      </>
                    ) : t('pdf.na', 'N/A')
                  )}
                </DetailsValue>

                <DetailsLabel>{t('pdf.deliveryDate', 'Delivery Date')}:</DetailsLabel>
                <DetailsValue>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={editingOrder?.deliveryDate ?? ''}
                      onChange={(e) => {
                        if (!editingOrder) return;
                        setEditingOrder({ ...editingOrder, deliveryDate: e.target.value });
                      }}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${ThemeColors.border.medium}`,
                        borderRadius: BorderRadius.small,
                        fontSize: Typography.fontSize.sm,
                        backgroundColor: 'white',
                        color: ThemeColors.text.primary,
                        width: '100%'
                      }}
                    />
                  ) : (
                    orderData.deliveryDate ? (
                      <>
                        <Clock size={12} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                        {formatOrderDateForDisplay(orderData.deliveryDate)}
                      </>
                    ) : t('pdf.na', 'N/A')
                  )}
                </DetailsValue>

                <DetailsLabel>{t('pdf.specialInstructions', 'Special Instructions')}:</DetailsLabel>
                <DetailsValue style={{ fontStyle: orderData.specialInstructions ? 'normal' : 'italic', color: orderData.specialInstructions ? ThemeColors.text.primary : ThemeColors.text.secondary }}>
                  {isEditMode ? (
                    <textarea
                      value={editingOrder?.specialInstructions ?? ''}
                      onChange={(e) => {
                        if (!editingOrder) return;
                        setEditingOrder({ ...editingOrder, specialInstructions: e.target.value });
                      }}
                      rows={3}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${ThemeColors.border.medium}`,
                        borderRadius: BorderRadius.small,
                        fontSize: Typography.fontSize.sm,
                        backgroundColor: 'white',
                        color: ThemeColors.text.primary,
                        width: '100%',
                        resize: 'vertical'
                      }}
                    />
                  ) : (
                    orderData.specialInstructions || t('pdf.na', 'N/A')
                  )}
                </DetailsValue>

                <DetailsLabel>{t('pdf.orderType', 'Order Type')}:</DetailsLabel>
                <DetailsValue style={{ textTransform: 'capitalize' }}>
                  {isEditMode ? (
                    <select
                      value={editingOrder?.orderType || 'custOrder'}
                      onChange={(e) => {
                        if (!editingOrder) return;
                        const nextOrderType = e.target.value === 'offCarDelivery' ? 'offCarDelivery' : 'custOrder';
                        setEditingOrder({
                          ...editingOrder,
                          orderType: nextOrderType
                        });
                      }}
                      style={{
                        padding: '6px 10px',
                        border: `1px solid ${ThemeColors.border.medium}`,
                        borderRadius: BorderRadius.small,
                        fontSize: Typography.fontSize.sm,
                        backgroundColor: 'white',
                        color: ThemeColors.text.primary
                      }}
                    >
                      <option value="custOrder">{t('forms.custOrder', 'Cust Order')}</option>
                      <option value="offCarDelivery">{t('forms.offCarDelivery', 'Off Car Delivery')}</option>
                    </select>
                  ) : (
                    orderData.orderType === 'custOrder' ? t('orderReport.customerOrder', 'Customer Order') : 
                    orderData.orderType === 'offCarDelivery' ? t('orderReport.offCarDelivery', 'Off Car Delivery') : 
                    orderData.orderType || t('pdf.na', 'N/A')
                  )}
                </DetailsValue>

                {orderData.finalizedAt && (
                  <>
                    <DetailsLabel>{t('orderReport.finalizedAt', 'Finalized At')}:</DetailsLabel>
                    <DetailsValue>
                      {orderData.finalizedAt instanceof Timestamp ? 
                        orderData.finalizedAt.toDate().toLocaleString() : 
                        new Date(orderData.finalizedAt).toLocaleString()}
                    </DetailsValue>
                  </>
                )}
              </DetailsGrid>
            </DetailsSection>

            {/* Sales Representative Section */}
            <DetailsSection>
              <DetailsTitle>
                <User size={18} />
                {t('orderReport.salesRepDetails', 'Sales Representative')}
              </DetailsTitle>
              <DetailsGrid>
                <DetailsLabel>{t('orderReport.salesRepName', 'Sales Rep Name')}:</DetailsLabel>
                <DetailsValue>{resolvedSubmitterDisplay || orderData.salesRepName || orderData.salesPersonName || t('pdf.na', 'N/A')}</DetailsValue>

                <DetailsLabel>{t('orderReport.salesRepNo', 'Sales Rep No')}:</DetailsLabel>
                <DetailsValue>{orderData.salespersonNo || orderData.salesRepId || t('pdf.na', 'N/A')}</DetailsValue>

                <DetailsLabel>{t('orderReport.salesRepEmail', 'Sales Rep Email')}:</DetailsLabel>
                <DetailsValue>
                  {(orderData.salesRepEmail || resolvedSubmitterEmail) ? (
                    <a href={`mailto:${orderData.salesRepEmail || resolvedSubmitterEmail || ''}`} style={{ color: ThemeColors.primary.main, textDecoration: 'none' }}>
                      <Mail size={12} style={{ marginRight: 4, display: 'inline-block', verticalAlign: 'middle' }} />
                      {orderData.salesRepEmail || resolvedSubmitterEmail}
                    </a>
                  ) : t('pdf.na', 'N/A')}
                </DetailsValue>

                {orderData.submittedAt && (
                  <>
                    <DetailsLabel>{t('orderReport.submittedAt', 'Submitted At')}:</DetailsLabel>
                    <DetailsValue>
                      {orderData.submittedAt && (orderData.submittedAt instanceof Timestamp
                        ? orderData.submittedAt.toDate().toLocaleString()
                        : new Date(orderData.submittedAt).toLocaleString())}
                    </DetailsValue>
                  </>
                )}
              </DetailsGrid>
            </DetailsSection>

            {/* Approval Section - shown when order is approved */}
            {(orderData.status === 'approved' || orderData.status === 'Approved') && (orderData.reviewedBy || orderData.reviewedByName) && (
              <DetailsSection>
                <DetailsTitle>
                  <CheckCircle size={18} />
                  {t('orderReport.approvalDetails', 'Approval')}
                </DetailsTitle>
                <DetailsGrid>
                  <DetailsLabel>{t('orderReport.approvedBy', 'Approved By')}:</DetailsLabel>
                  <DetailsValue>{orderData.reviewedByName || resolvedManagerDisplay || orderData.reviewedBy || t('pdf.na', 'N/A')}</DetailsValue>

                  {orderData.reviewedAt && (
                    <>
                      <DetailsLabel>{t('orderReport.approvedAt', 'Approved At')}:</DetailsLabel>
                      <DetailsValue>
                        {orderData.reviewedAt instanceof Timestamp
                          ? orderData.reviewedAt.toDate().toLocaleString()
                          : new Date(orderData.reviewedAt).toLocaleString()}
                      </DetailsValue>
                    </>
                  )}
                </DetailsGrid>
              </DetailsSection>
            )}
          </DetailsContainer>

          {/* Filters */}
          <FiltersBar>
            <SearchBar>
              <Search />
              <SearchInput
                type="text"
                placeholder={t('orderReport.searchPlaceholder', 'Search products...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchBar>
            <FilterSelect value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="all">{t('orderReport.allCategories', 'All Categories')}</option>
              <option value="H">{t('categories.heritage')}</option>
              <option value="L">Livingston</option>
              <option value="F">{t('categories.flower')}</option>
              <option value="V">{t('categories.vegetable')}</option>
              <option value="S">{t('categories.supplies', 'Supplies')}</option>
            </FilterSelect>
          </FiltersBar>

          {/* Table */}
          <TableContainer>
            <TableWrapper>
              <Table>
                <TableHeader>
                  <tr>
                    <SortableHeader onClick={() => handleSort('category')}>
                      <SortableHeaderContent>
                        {t('orderReport.category', 'Category')}
                        {sortField === 'category' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </SortableHeaderContent>
                    </SortableHeader>
                    <SortableHeader onClick={() => handleSort('name')}>
                      <SortableHeaderContent>
                        {t('orderReport.product', 'Product')}
                        {sortField === 'name' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </SortableHeaderContent>
                    </SortableHeader>
                    <th>{t('orderReport.code', 'Code')}</th>
                    <SortableHeader onClick={() => handleSort('quantity')}>
                      <SortableHeaderContent>
                        {t('orderReport.quantity', 'Quantity')}
                        {sortField === 'quantity' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </SortableHeaderContent>
                    </SortableHeader>
                    <SortableHeader onClick={() => handleSort('price')}>
                      <SortableHeaderContent>
                        {t('orderReport.unitPrice', 'Unit Price')}
                        {sortField === 'price' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </SortableHeaderContent>
                    </SortableHeader>
                    <SortableHeader onClick={() => handleSort('total')}>
                      <SortableHeaderContent>
                        {t('orderReport.total', 'Total')}
                        {sortField === 'total' && (sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)}
                      </SortableHeaderContent>
                    </SortableHeader>
                  </tr>
                </TableHeader>
                <TableBody>
                  {filteredAndSortedItems.length === 0 ? (
                    <tr>
                      <td colSpan={6}>
                        <EmptyState>
                          {t('orderReport.noItems', 'No items found matching your filters')}
                        </EmptyState>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredAndSortedItems.map((item, index) => {
                        const previousItem = index > 0 ? filteredAndSortedItems[index - 1] : null;
                        const showCategoryTotal = previousItem && previousItem.category !== item.category;
                        const isLastInCategory = index === filteredAndSortedItems.length - 1 || 
                          filteredAndSortedItems[index + 1].category !== item.category;
                        
                        const itemTotal = (parseFloat(item.price) || 0) * (parseInt(item.quantity) || 0);
                        
                        return (
                          <React.Fragment key={index}>
                            {showCategoryTotal && previousItem && (
                              <SummaryRow>
                                <td colSpan={3}>
                                  {previousItem.categoryName || previousItem.category} {t('orderReport.subtotal', 'Subtotal')}
                                </td>
                                <td>
                                  {totals.categoryTotals[previousItem.category]?.items || 0}
                                </td>
                                <td></td>
                                <td>
                                  ${(totals.categoryTotals[previousItem.category]?.amount || 0).toFixed(2)}
                                </td>
                              </SummaryRow>
                            )}
                            <tr>
                              <td>
                                <CategoryBadge category={item.category}>
                                  {item.category}
                                </CategoryBadge>
                              </td>
                              <td>
                                {getTranslatedProductName(item.packetDescription || item.name || '', t)}
                              </td>
                              <td>{item.code || '-'}</td>
                              <td>
                                {isEditMode ? (
                                  <input
                                    type="number"
                                    min="0"
                                    value={item.quantity || 0}
                                    onChange={(e) => {
                                      const newQuantity = parseInt(e.target.value) || 0;
                                      const categoryKey = item.category === 'H' ? 'heritageItems' :
                                                         item.category === 'L' ? 'livingstonItems' :
                                                         item.category === 'F' ? 'flowerItems' :
                                                         item.category === 'V' ? 'vegetableItems' :
                                                         'supplyItems';
                                      
                                      if (!editingOrder) return;
                                      
                                      const updatedItems = [...(editingOrder[categoryKey] || [])];
                                      const itemIndex = updatedItems.findIndex(i => 
                                        (i.code === item.code || i.id === item.id) &&
                                        (i.packetDescription === item.packetDescription || i.name === item.name)
                                      );
                                      
                                      if (itemIndex >= 0) {
                                        if (newQuantity > 0) {
                                          // Ensure quantity is a number, not a string
                                          updatedItems[itemIndex] = { 
                                            ...updatedItems[itemIndex], 
                                            quantity: Number(newQuantity) 
                                          };
                                        } else {
                                          updatedItems.splice(itemIndex, 1);
                                        }
                                        
                                        setEditingOrder({
                                          ...editingOrder,
                                          [categoryKey]: updatedItems
                                        });
                                      }
                                    }}
                                    style={{
                                      width: '70px',
                                      padding: '6px 8px',
                                      border: `1px solid ${ThemeColors.border.medium}`,
                                      borderRadius: '4px',
                                      fontSize: Typography.fontSize.sm,
                                      textAlign: 'center'
                                    }}
                                  />
                                ) : (
                                  item.quantity || 0
                                )}
                              </td>
                              <td>${(parseFloat(item.price) || 0).toFixed(2)}</td>
                              <td>${itemTotal.toFixed(2)}</td>
                            </tr>
                            {isLastInCategory && index === filteredAndSortedItems.length - 1 && (
                              <SummaryRow>
                                <td colSpan={3}>
                                  {item.categoryName || item.category} {t('orderReport.subtotal', 'Subtotal')}
                                </td>
                                <td>
                                  {totals.categoryTotals[item.category]?.items || 0}
                                </td>
                                <td></td>
                                <td>
                                  ${(totals.categoryTotals[item.category]?.amount || 0).toFixed(2)}
                                </td>
                              </SummaryRow>
                            )}
                          </React.Fragment>
                        );
                      })}
                      <TotalRow>
                        <td colSpan={3}>
                          <strong>{t('orderReport.grandTotal', 'Grand Total')}</strong>
                        </td>
                        <td>
                          <strong>{totals.totalItems}</strong>
                        </td>
                        <td></td>
                        <td>
                          <strong>${totals.totalAmount.toFixed(2)}</strong>
                        </td>
                      </TotalRow>
                    </>
                  )}
                </TableBody>
              </Table>
            </TableWrapper>
          </TableContainer>
        </Container>

        <style>{`
          .spin {
            animation: spin 1s linear infinite;
          }
          
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>

        {/* Product Search Modal */}
        {showProductModal && (
          <ModalOverlay onClick={handleCloseProductModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>
                  <ShoppingCart size={24} />
                  {t('orderReport.addProductsTitle', 'Add Products to Order')}
                </ModalTitle>
                <CloseButton onClick={handleCloseProductModal}>
                  <X size={20} />
                </CloseButton>
              </ModalHeader>
              
              <ModalBody>
                <ProductSearchBar>
                  <ProductSearchInput
                    type="text"
                    placeholder={t('orderReport.searchProductsPlaceholder', 'Search by product name or item code...')}
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    autoFocus
                  />
                  <ProductCategorySelect
                    value={productCategoryFilter}
                    onChange={(e) => setProductCategoryFilter(e.target.value)}
                  >
                    <option value="all">{t('orderReport.allCategories', 'All Categories')}</option>
                    <option value="Heritage">{t('categories.heritage', 'Heritage')}</option>
                    <option value="Livingston">Livingston</option>
                    <option value="Flower">{t('categories.flower', 'Flower')}</option>
                    <option value="Vegetable">{t('categories.vegetable', 'Vegetable')}</option>
                  </ProductCategorySelect>
                </ProductSearchBar>
                
                {loadingProducts ? (
                  <LoadingProducts>
                    <Loader size={40} className="spin" color={ThemeColors.primary.main} />
                    <div>{t('orderReport.loadingProducts', 'Loading products...')}</div>
                  </LoadingProducts>
                ) : filteredProducts.length === 0 ? (
                  <NoProductsMessage>
                    {productSearchTerm 
                      ? t('orderReport.noProductsFound', 'No products found matching your search')
                      : t('orderReport.startSearching', 'Start typing to search for products')}
                  </NoProductsMessage>
                ) : (
                  <ProductGrid>
                    {filteredProducts.map((product) => {
                      const selectedQty = selectedProducts.get(product.ItemCode) || 0;
                      const isSelected = selectedQty > 0;
                      
                      return (
                        <ProductCard 
                          key={product.ItemCode}
                          isSelected={isSelected}
                          onClick={() => {
                            if (!isSelected) {
                              incrementQuantity(product.ItemCode);
                            }
                          }}
                        >
                          <ProductCardHeader>
                            <ProductName>
                              {t('language') === 'fr' ? product.ItemNameFR : product.ItemNameEN}
                            </ProductName>
                            <ProductCode>{product.ItemCode}</ProductCode>
                          </ProductCardHeader>
                          
                          <ProductDetails>
                            <ProductCategory category={product.ItemForm}>
                              {product.ItemForm}
                            </ProductCategory>
                            <ProductPrice>${product.Price.toFixed(2)}</ProductPrice>
                          </ProductDetails>
                          
                          {isSelected && (
                            <QuantityControl onClick={(e) => e.stopPropagation()}>
                              <QuantityButton 
                                onClick={() => decrementQuantity(product.ItemCode)}
                              >
                                <Minus size={14} />
                              </QuantityButton>
                              <QuantityInput
                                type="number"
                                min="0"
                                value={selectedQty}
                                onChange={(e) => handleProductQuantityChange(product.ItemCode, parseInt(e.target.value) || 0)}
                              />
                              <QuantityButton 
                                onClick={() => incrementQuantity(product.ItemCode)}
                              >
                                <Plus size={14} />
                              </QuantityButton>
                            </QuantityControl>
                          )}
                        </ProductCard>
                      );
                    })}
                  </ProductGrid>
                )}
              </ModalBody>
              
              <ModalFooter>
                <SelectedProductsInfo>
                  <ShoppingCart size={18} />
                  {selectedProducts.size > 0 
                    ? t('orderReport.selectedProducts', '{{count}} product(s) selected', { count: selectedProducts.size })
                    : t('orderReport.noProductsSelected', 'No products selected')}
                  {selectedProducts.size > 0 && (
                    <span style={{ marginLeft: Spacing.md, fontWeight: Typography.fontWeight.semiBold }}>
                      ({Array.from(selectedProducts.values()).reduce((a, b) => a + b, 0)} {t('orderReport.totalUnits', 'total units')})
                    </span>
                  )}
                </SelectedProductsInfo>
                <AddProductsButton
                  onClick={handleAddProductsToOrder}
                  disabled={selectedProducts.size === 0}
                >
                  <Plus size={18} />
                  {t('orderReport.addToOrder', 'Add to Order')}
                </AddProductsButton>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </MainContent>
    </PageContainer>
  );
};

